import { create } from 'zustand'
import type { FilterGroup } from '@/tableControls'
import type {
  EmailSearchResult,
  EmailSearchRequest,
  SearchMode,
  ProcessedEmailInfo,
  GlobalFilters,
  ManualQueryFilters,
  EmailSearchReviewStatusFilter,
  EmailSearchResultFilters,
  RuleQuickFilter,
} from '@/types/emailSearch'

const SESSION_TTL_MS = 10 * 60 * 1000 // 10 minutes

const emptyGlobalFilters: GlobalFilters = {
  inbox: '',
  start_date: '',
  end_date: '',
  limit: undefined,
}

const emptyManualFilters: ManualQueryFilters = {
  from: '',
  to: '',
  subject: '',
  has_words: '',
  doesnt_have: '',
  has_attachment: undefined,
}

const emptyResultFilters: EmailSearchResultFilters = {
  subject: '',
  from: '',
  inbox: '',
  hasAttachment: '',
}

const defaultFormData: EmailSearchRequest = {
  from: '',
  to: '',
  subject: '',
  has_words: '',
  doesnt_have: '',
  start_date: '',
  end_date: '',
  has_attachment: undefined,
  inbox: '',
  limit: undefined,
}

interface EmailSearchState {
  results: EmailSearchResult[]
  queryUsed: string
  /** True when the current results came from a rule-based search (not manual Gmail query). */
  lastSearchUsedRules: boolean
  formData: EmailSearchRequest
  cachedAt: number | null
  searchMode: SearchMode
  selectedRuleIds: number[]
  processedInfo: Record<string, ProcessedEmailInfo>

  globalFilters: GlobalFilters
  manualFilters: ManualQueryFilters
  rawGmailQuery: string
  resultFilters: EmailSearchResultFilters
  resultStatusFilter: EmailSearchReviewStatusFilter
  resultFilterTree: FilterGroup | null
  resultTextSearch: string
  quickFilter: RuleQuickFilter | null
  ruleSearch: string
  /** Result table row selection (message IDs). */
  selectedResultMessageIds: string[]

  /** Sliding window: any activity on this page refreshes the expiry. */
  lastActivityAt: number | null
  /** Set when a search request finishes (including 0 hits). */
  lastSearchCompletedAt: number | null

  setResults: (results: EmailSearchResult[], queryUsed: string, fromRuleSearch: boolean) => void
  /** Clears result list and processed flags when the user starts a new search (does not clear sidebar filters). */
  beginNewSearch: () => void
  setFormData: (formData: EmailSearchRequest) => void
  setSearchMode: (mode: SearchMode) => void
  setSelectedRuleIds: (ids: number[]) => void
  setProcessedInfo: (info: Record<string, ProcessedEmailInfo>) => void

  setGlobalFilters: (filters: GlobalFilters | ((prev: GlobalFilters) => GlobalFilters)) => void
  setManualFilters: (filters: ManualQueryFilters | ((prev: ManualQueryFilters) => ManualQueryFilters)) => void
  setRawGmailQuery: (q: string) => void
  setResultFilters: (
    filters: EmailSearchResultFilters | ((prev: EmailSearchResultFilters) => EmailSearchResultFilters)
  ) => void
  setResultStatusFilter: (f: EmailSearchReviewStatusFilter) => void
  setResultFilterTree: (tree: FilterGroup | null) => void
  setResultTextSearch: (q: string) => void
  setQuickFilter: (q: RuleQuickFilter | null) => void
  setRuleSearch: (s: string) => void
  setSelectedResultMessageIds: (ids: string[]) => void

  clearCache: () => void
  /** @deprecated Prefer isSessionValid */
  isCacheValid: () => boolean
  isSessionValid: () => boolean
  /** If the session is past TTL, reset all email-search state. Call on mount and periodically. */
  expireSessionIfStale: () => boolean
}

function touchActivity(set: (partial: Partial<EmailSearchState>) => void) {
  set({ lastActivityAt: Date.now() })
}

export const useEmailSearchStore = create<EmailSearchState>((set, get) => ({
  results: [],
  queryUsed: '',
  lastSearchUsedRules: false,
  formData: defaultFormData,
  cachedAt: null,
  searchMode: 'rules',
  selectedRuleIds: [],
  processedInfo: {},

  globalFilters: { ...emptyGlobalFilters },
  manualFilters: { ...emptyManualFilters },
  rawGmailQuery: '',
  resultFilters: { ...emptyResultFilters },
  resultStatusFilter: 'all',
  resultFilterTree: null,
  resultTextSearch: '',
  quickFilter: null,
  ruleSearch: '',
  selectedResultMessageIds: [],

  lastActivityAt: null,
  lastSearchCompletedAt: null,

  setResults: (results, queryUsed, fromRuleSearch) => {
    const now = Date.now()
    set({
      results,
      queryUsed,
      lastSearchUsedRules: fromRuleSearch,
      cachedAt: now,
      lastActivityAt: now,
      lastSearchCompletedAt: now,
    })
  },

  beginNewSearch: () =>
    set({
      results: [],
      queryUsed: '',
      lastSearchUsedRules: false,
      cachedAt: null,
      processedInfo: {},
      lastSearchCompletedAt: null,
      resultTextSearch: '',
      resultFilterTree: null,
      lastActivityAt: Date.now(),
    }),

  setFormData: (formData) => {
    set({ formData, lastActivityAt: Date.now() })
  },

  setSearchMode: (mode) => {
    touchActivity(set)
    set({ searchMode: mode })
  },

  setSelectedRuleIds: (ids) => {
    touchActivity(set)
    set({ selectedRuleIds: ids })
  },

  setProcessedInfo: (info) => set({ processedInfo: info }),

  setGlobalFilters: (filters) => {
    touchActivity(set)
    set((state) => ({
      globalFilters: typeof filters === 'function' ? filters(state.globalFilters) : filters,
    }))
  },

  setManualFilters: (filters) => {
    touchActivity(set)
    set((state) => ({
      manualFilters: typeof filters === 'function' ? filters(state.manualFilters) : filters,
    }))
  },

  setRawGmailQuery: (rawGmailQuery) => {
    touchActivity(set)
    set({ rawGmailQuery })
  },

  setResultFilters: (filters) => {
    touchActivity(set)
    set((state) => ({
      resultFilters: typeof filters === 'function' ? filters(state.resultFilters) : filters,
    }))
  },

  setResultStatusFilter: (resultStatusFilter) => {
    touchActivity(set)
    set({ resultStatusFilter })
  },

  setResultFilterTree: (resultFilterTree) => {
    touchActivity(set)
    set({ resultFilterTree })
  },

  setResultTextSearch: (resultTextSearch) => {
    touchActivity(set)
    set({ resultTextSearch })
  },

  setQuickFilter: (quickFilter) => {
    touchActivity(set)
    set({ quickFilter })
  },

  setRuleSearch: (ruleSearch) => {
    touchActivity(set)
    set({ ruleSearch })
  },

  setSelectedResultMessageIds: (selectedResultMessageIds) => {
    touchActivity(set)
    set({ selectedResultMessageIds })
  },

  clearCache: () =>
    set({
      results: [],
      queryUsed: '',
      lastSearchUsedRules: false,
      cachedAt: null,
      processedInfo: {},
      globalFilters: { ...emptyGlobalFilters },
      manualFilters: { ...emptyManualFilters },
      rawGmailQuery: '',
      resultFilters: { ...emptyResultFilters },
      resultStatusFilter: 'all',
      resultFilterTree: null,
      resultTextSearch: '',
      quickFilter: null,
      ruleSearch: '',
      selectedRuleIds: [],
      selectedResultMessageIds: [],
      lastActivityAt: null,
      lastSearchCompletedAt: null,
    }),

  isCacheValid: () => get().isSessionValid(),

  isSessionValid: () => {
    const { lastActivityAt, cachedAt } = get()
    const t = lastActivityAt ?? cachedAt
    if (t == null) return false
    return Date.now() - t < SESSION_TTL_MS
  },

  expireSessionIfStale: () => {
    const s = get()
    const t = s.lastActivityAt ?? s.cachedAt
    if (t == null) return false
    if (Date.now() - t < SESSION_TTL_MS) return false
    get().clearCache()
    return true
  },
}))
